<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceJsonBody,
} from 'dashboard/helpers/marketplaceApi';
import OrderDeductionsPanel from '../components/OrderDeductionsPanel.vue';

const route = useRoute();
const accountId = route.params.accountId;

const loading = ref(true);
const error = ref('');
const channels = ref([]);
const detective = reactive({});

// Eşleme Masası
const activeChannelId = ref(null);
const matchesLoading = ref(false);
const rows = ref([]);
const candidates = ref([]);
const busyIds = ref(new Set());
const bulkBusy = ref(false);
const toast = ref('');

// Elle seçim / renk eşleme satır durumları
const manualPick = reactive({}); // externalId -> productId
const colorPick = reactive({}); // externalId -> { channelColor: umColor }

const shopifyChannel = computed(() =>
  channels.value.find(c => c.channelType === 'shopify_adrom')
);
const highConfidenceCount = computed(
  () => rows.value.filter(r => (r.suggestion?.confidence || 0) >= 0.9).length
);
const unmatchedCount = computed(
  () => rows.value.filter(r => !r.suggestion).length
);
const colorReviewCount = computed(
  () =>
    rows.value.filter(r => (r.suggestion?.unmatchedColors || []).length > 0)
      .length
);

function confBand(c) {
  if (c >= 0.9)
    return {
      cls: 'bg-green-500',
      label: 'otomatik',
      pill: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    };
  if (c >= 0.6)
    return {
      cls: 'bg-yellow-500',
      label: 'onay bekliyor',
      pill: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
    };
  return {
    cls: 'bg-red-500',
    label: 'düşük',
    pill: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  };
}

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const data = await fetchMarketplaceJson(accountId, 'channels');
    channels.value = data.channels || [];
    Object.assign(detective, data.detective || {});
    if (shopifyChannel.value) {
      activeChannelId.value = shopifyChannel.value.id;
      await loadMatches();
    }
  } catch (e) {
    error.value = 'Kanallar yüklenemedi: ' + e.message;
  } finally {
    loading.value = false;
  }
}

async function loadMatches() {
  if (!activeChannelId.value) return;
  matchesLoading.value = true;
  try {
    const data = await fetchMarketplaceJson(
      accountId,
      `channels/${activeChannelId.value}/matches`
    );
    rows.value = data.rows || [];
    candidates.value = data.candidates || [];
    // Renk seçim sözlüklerini önceden aç (template'te güvenli v-model için)
    rows.value.forEach(r => {
      if (
        r.suggestion?.unmatchedColors?.length &&
        !colorPick[r.channelProduct.id]
      ) {
        colorPick[r.channelProduct.id] = {};
      }
    });
  } catch (e) {
    error.value = 'Eşleme masası yüklenemedi: ' + e.message;
  } finally {
    matchesLoading.value = false;
  }
}

function setBusy(id, v) {
  const s = new Set(busyIds.value);
  if (v) s.add(id);
  else s.delete(id);
  busyIds.value = s;
}

async function confirmRow(row) {
  const ext = row.channelProduct.id;
  const productId = row.suggestion ? row.suggestion.productId : manualPick[ext];
  if (!productId) return;
  setBusy(ext, true);
  try {
    await postMarketplaceJsonBody(
      accountId,
      `channels/${activeChannelId.value}/confirm`,
      {
        externalProductId: ext,
        productId,
        colorMap: colorPick[ext] || {},
        confidence: row.suggestion?.confidence,
        matchStatus: row.suggestion ? 'confirmed' : 'manual',
      }
    );
    rows.value = rows.value.filter(r => r.channelProduct.id !== ext);
    toast.value = `Bağ kuruldu: ${row.channelProduct.title}`;
    refreshChannelStats();
  } catch (e) {
    error.value = 'Bağ kurulamadı: ' + e.message;
  } finally {
    setBusy(ext, false);
  }
}

async function rejectRow(row) {
  const ext = row.channelProduct.id;
  if (!row.suggestion) return;
  setBusy(ext, true);
  try {
    await postMarketplaceJsonBody(
      accountId,
      `channels/${activeChannelId.value}/reject`,
      { externalProductId: ext, productId: row.suggestion.productId }
    );
    rows.value = rows.value.filter(r => r.channelProduct.id !== ext);
  } catch (e) {
    error.value = 'Reddedilemedi: ' + e.message;
  } finally {
    setBusy(ext, false);
  }
}

async function bulkConfirm() {
  bulkBusy.value = true;
  try {
    const res = await postMarketplaceJsonBody(
      accountId,
      `channels/${activeChannelId.value}/bulk_confirm`,
      { minConfidence: 0.9 }
    );
    toast.value = `${res.confirmed} ürünün bağı kuruldu.`;
    await loadMatches();
    refreshChannelStats();
  } catch (e) {
    error.value = 'Toplu onay hatası: ' + e.message;
  } finally {
    bulkBusy.value = false;
  }
}

async function refreshChannelStats() {
  try {
    const data = await fetchMarketplaceJson(accountId, 'channels');
    channels.value = data.channels || [];
  } catch {
    /* sessiz */
  }
}

onMounted(load);
</script>

<template>
  <div
    class="flex flex-col h-full flex-1 min-w-0 overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <!-- Başlık -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0"
    >
      <div>
        <h1 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Entegrasyonlar
        </h1>
        <p class="text-xs text-slate-400">
          Ürün Merkezi → satış kanalları · tek gerçek kaynak buradadır
        </p>
      </div>
      <button
        class="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50"
        :disabled="matchesLoading"
        @click="loadMatches"
      >
        <span
          :class="
            matchesLoading
              ? 'i-lucide-loader-2 animate-spin'
              : 'i-lucide-refresh-cw'
          "
          class="size-4"
        />
        Yenile
      </button>
    </div>

    <div class="flex-1 overflow-auto">
      <div
        v-if="loading"
        class="flex h-48 items-center justify-center gap-2 text-slate-400"
      >
        <span class="i-lucide-loader-2 size-5 animate-spin" />
        <span class="text-sm">Kanallar yükleniyor…</span>
      </div>

      <template v-else>
        <div
          v-if="error"
          class="m-5 flex items-center gap-3 rounded-xl bg-red-50 dark:bg-red-950 p-4 text-sm text-red-600 dark:text-red-400"
        >
          <span class="i-lucide-alert-circle size-5 shrink-0" />
          <span class="flex-1">{{ error }}</span>
          <button class="text-xs font-semibold" @click="error = ''">✕</button>
        </div>
        <div
          v-if="toast"
          class="m-5 mb-0 flex items-center gap-3 rounded-xl bg-green-50 dark:bg-green-900/20 p-3 text-sm text-green-700 dark:text-green-300"
        >
          <span class="i-lucide-check-circle size-4 shrink-0" />
          <span class="flex-1">{{ toast }}</span>
          <button class="text-xs font-semibold" @click="toast = ''">✕</button>
        </div>

        <!-- Kanal kartları -->
        <div
          class="grid gap-4 px-5 pt-5"
          style="grid-template-columns: repeat(auto-fit, minmax(300px, 1fr))"
        >
          <!-- Shopify -->
          <div
            v-if="shopifyChannel"
            class="rounded-2xl border border-green-200 dark:border-green-900 bg-white dark:bg-slate-900 p-4 flex flex-col gap-3"
          >
            <div class="flex items-center gap-2.5">
              <div
                class="grid size-9 place-items-center rounded-lg text-white font-bold"
                style="background: #5e8e3e"
              >
                S
              </div>
              <div class="min-w-0">
                <b class="text-[13.5px] text-slate-800 dark:text-slate-100">{{
                  shopifyChannel.name
                }}</b>
                <div class="text-[11px] text-slate-400">
                  adromfashion.com · USD
                </div>
              </div>
              <span
                class="ml-auto rounded-full bg-green-100 dark:bg-green-900/40 px-2 py-0.5 text-[10.5px] font-bold text-green-700 dark:text-green-300"
                >● Bağlı</span>
            </div>
            <div class="grid grid-cols-3 gap-1.5">
              <div
                class="rounded-lg bg-slate-50 dark:bg-slate-800 px-2 py-1.5 text-center"
              >
                <b class="block font-mono text-base text-green-600">{{
                  shopifyChannel.stats.linked
                }}</b>
                <span
                  class="text-[9.5px] font-semibold uppercase text-slate-400"
                  >bağlı</span>
              </div>
              <div
                class="rounded-lg bg-slate-50 dark:bg-slate-800 px-2 py-1.5 text-center"
              >
                <b
                  class="block font-mono text-base"
                  :class="
                    (shopifyChannel.stats.unmatched || 0) > 0
                      ? 'text-yellow-600'
                      : 'text-slate-600'
                  "
                  >{{ shopifyChannel.stats.unmatched ?? '—' }}</b>
                <span
                  class="text-[9.5px] font-semibold uppercase text-slate-400"
                  >eşleşmemiş</span>
              </div>
              <div
                class="rounded-lg bg-slate-50 dark:bg-slate-800 px-2 py-1.5 text-center"
              >
                <b
                  class="block font-mono text-base"
                  :class="
                    shopifyChannel.stats.error > 0
                      ? 'text-red-600'
                      : 'text-slate-600'
                  "
                  >{{ shopifyChannel.stats.error }}</b>
                <span
                  class="text-[9.5px] font-semibold uppercase text-slate-400"
                  >hata</span>
              </div>
            </div>
            <div class="flex flex-wrap gap-1.5 text-[10.5px] font-semibold">
              <span
                class="rounded-full bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2.5 py-1"
                >Stok: otomatik</span>
              <span
                class="rounded-full bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2.5 py-1"
                >Fiyat: otomatik</span>
              <span
                class="rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 px-2.5 py-1"
                >İçerik: elle gönder</span>
            </div>
            <p class="text-[11px] text-slate-400">
              Kanalda {{ shopifyChannel.stats.externalTotal ?? '—' }} ürün ·
              stok/fiyat/içerik gönderimi Faz 2-3'te açılacak.
            </p>
          </div>

          <!-- Ticimax (kaynak) -->
          <div
            class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex flex-col gap-3"
          >
            <div class="flex items-center gap-2.5">
              <div
                class="grid size-9 place-items-center rounded-lg bg-slate-900 dark:bg-slate-700 text-white font-bold"
              >
                T
              </div>
              <div>
                <b class="text-[13.5px] text-slate-800 dark:text-slate-100">Ticimax · Koza</b>
                <div class="text-[11px] text-slate-400">
                  stok + maliyet kaynağı
                </div>
              </div>
              <span
                class="ml-auto rounded-full bg-woot-50 dark:bg-woot-900/30 px-2 py-0.5 text-[10.5px] font-bold text-woot-700 dark:text-woot-300"
                >KAYNAK</span>
            </div>
            <div class="flex flex-wrap gap-1.5 text-[10.5px] font-semibold">
              <span
                class="rounded-full bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2.5 py-1"
                >Stok çek: saatlik ✓</span>
              <span
                class="rounded-full bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2.5 py-1"
                >Sipariş düş: onaylı mod ✓</span>
            </div>
            <p class="text-[11px] text-slate-400">
              1032 ürün · 24.5k varyant canlı akıyor. Buraya içerik gönderilmez
              — tek yönlü kaynak.
            </p>
          </div>

          <!-- Trendyol (yakında) -->
          <div
            class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex flex-col gap-3 opacity-60"
          >
            <div class="flex items-center gap-2.5">
              <div
                class="grid size-9 place-items-center rounded-lg text-white font-bold"
                style="background: #f27a1a"
              >
                t
              </div>
              <div>
                <b class="text-[13.5px] text-slate-800 dark:text-slate-100">Trendyol · Koza</b>
                <div class="text-[11px] text-slate-400">
                  aynı boru hattına takılır
                </div>
              </div>
              <span
                class="ml-auto rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10.5px] font-bold text-slate-400"
                >YAKINDA</span>
            </div>
            <p class="text-[11px] text-slate-400 mt-auto">
              Mimari hazır: adapter yaz → kanal kartı aç. Hepsiburada / Amazon
              da aynı yolla.
            </p>
          </div>
        </div>

        <!-- Kanal Dedektifi -->
        <div
          class="mx-5 mt-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3.5 flex flex-wrap items-center gap-2.5"
        >
          <span
            class="flex items-center gap-2 text-[12.5px] font-bold text-slate-700 dark:text-slate-200 mr-1"
          >
            <span
              class="grid size-6 place-items-center rounded-md bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 text-xs"
              >🕵</span>
            Kanal Dedektifi
          </span>
          <span
            class="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5"
          >
            <b
              class="font-mono text-sm"
              :class="unmatchedCount ? 'text-red-600' : 'text-green-600'"
              >{{ unmatchedCount }}</b>
            <span class="text-[11.5px] text-slate-500">ürün önerisiz</span>
          </span>
          <span
            class="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5"
          >
            <b class="font-mono text-sm text-yellow-600">{{
              colorReviewCount
            }}</b>
            <span class="text-[11.5px] text-slate-500">üründe renk onayı bekliyor</span>
          </span>
          <span
            class="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5"
          >
            <b class="font-mono text-sm text-yellow-600">{{
              detective.priceless ?? '—'
            }}</b>
            <span class="text-[11.5px] text-slate-500">üründe toptan fiyat yok</span>
          </span>
          <span
            class="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5"
          >
            <b
              class="font-mono text-sm"
              :class="detective.errors ? 'text-red-600' : 'text-green-600'"
              >{{ detective.errors ?? 0 }}</b>
            <span class="text-[11.5px] text-slate-500">gönderim hatası</span>
          </span>
        </div>

        <!-- Sipariş Düşümü (Faz 4) — asıl ekran: Toptan > Siparişler -->
        <OrderDeductionsPanel
          v-if="shopifyChannel"
          :channel-id="shopifyChannel.id"
          class="mx-5 mt-4"
        />

        <!-- Eşleme Masası -->
        <div
          class="mx-5 my-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
        >
          <div
            class="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800"
          >
            <h2
              class="text-sm font-semibold text-slate-800 dark:text-slate-200"
            >
              Eşleme Masası
              <span class="font-mono text-xs text-slate-400">{{
                rows.length
              }}</span>
            </h2>
            <p class="text-[11.5px] text-slate-400 hidden sm:block">
              Onaylanan bağ kalıcıdır — stok/fiyat/içerik bu bağdan akar.
            </p>
            <button
              class="ml-auto px-3.5 py-2 text-xs font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-40"
              :disabled="bulkBusy || !highConfidenceCount"
              @click="bulkConfirm"
            >
              <span
                v-if="bulkBusy"
                class="i-lucide-loader-2 size-3.5 animate-spin inline-block align-middle mr-1"
              />
              ✓ %90+ olanların tümünü onayla ({{ highConfidenceCount }})
            </button>
          </div>

          <div
            v-if="matchesLoading"
            class="flex h-40 items-center justify-center gap-2 text-slate-400"
          >
            <span class="i-lucide-loader-2 size-5 animate-spin" />
            <span class="text-sm">Kanal ürünleri taranıyor…</span>
          </div>

          <div
            v-else-if="!rows.length"
            class="flex flex-col items-center justify-center gap-2 py-14 text-slate-400"
          >
            <span class="i-lucide-check-circle-2 size-10 text-green-500" />
            <p class="text-sm font-medium text-slate-600 dark:text-slate-300">
              Eşleşmemiş ürün kalmadı 🎉
            </p>
            <p class="text-xs">Kanaldaki her ürün Ürün Merkezi'ne bağlı.</p>
          </div>

          <div v-else class="divide-y divide-slate-100 dark:divide-slate-800">
            <div
              v-for="row in rows"
              :key="row.channelProduct.id"
              class="px-5 py-3.5"
            >
              <div class="flex flex-wrap items-center gap-3">
                <!-- kanal ürünü -->
                <div class="min-w-0 flex-1 basis-64">
                  <a
                    :href="row.channelProduct.url"
                    target="_blank"
                    class="text-[13px] font-semibold text-slate-800 dark:text-slate-100 hover:text-woot-600 leading-tight"
                  >
                    {{ row.channelProduct.title }} ↗
                  </a>
                  <div class="mt-0.5 text-[11px] text-slate-400">
                    Shopify · {{ row.channelProduct.variantCount }} varyant ·
                    {{ row.channelProduct.colors.join(', ') || 'renk yok' }}
                  </div>
                </div>

                <!-- öneri -->
                <div class="min-w-0 flex-1 basis-72">
                  <template v-if="row.suggestion">
                    <div
                      class="text-[12.5px] text-slate-700 dark:text-slate-200 leading-tight"
                    >
                      → {{ row.suggestion.productTitle }}
                    </div>
                    <div
                      class="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400"
                    >
                      <span class="font-mono">{{
                        row.suggestion.parentCode
                      }}</span>
                      <span>renk {{ row.suggestion.matchedColors }}/{{
                          row.suggestion.totalColors
                        }}</span>
                    </div>
                  </template>
                  <template v-else>
                    <select
                      v-model="manualPick[row.channelProduct.id]"
                      class="w-full max-w-xs px-2 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                    >
                      <option :value="undefined">
                        Ürün Merkezi'nden elle seç…
                      </option>
                      <option v-for="c in candidates" :key="c.id" :value="c.id">
                        {{ c.title }}
                      </option>
                    </select>
                  </template>
                </div>

                <!-- güven -->
                <div class="w-24 shrink-0">
                  <template v-if="row.suggestion">
                    <div class="flex items-center gap-1.5">
                      <span
                        class="h-1.5 w-12 overflow-hidden rounded bg-slate-100 dark:bg-slate-800"
                      >
                        <i
                          class="block h-full rounded"
                          :class="confBand(row.suggestion.confidence).cls"
                          :style="{
                            width:
                              Math.round(row.suggestion.confidence * 100) + '%',
                          }"
                        />
                      </span>
                      <b class="font-mono text-[11.5px]">%{{ Math.round(row.suggestion.confidence * 100) }}</b>
                    </div>
                    <span
                      class="mt-1 inline-block rounded-full px-2 py-0.5 text-[9.5px] font-bold"
                      :class="confBand(row.suggestion.confidence).pill"
                    >
                      {{ confBand(row.suggestion.confidence).label }}
                    </span>
                  </template>
                  <span
                    v-else
                    class="inline-block rounded-full bg-red-100 dark:bg-red-900/40 px-2 py-0.5 text-[9.5px] font-bold text-red-700 dark:text-red-300"
                    >eşleşmedi</span>
                </div>

                <!-- işlemler -->
                <div class="flex shrink-0 items-center gap-1.5">
                  <button
                    class="px-2.5 py-1.5 text-[11.5px] font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-40"
                    :disabled="
                      busyIds.has(row.channelProduct.id) ||
                      (!row.suggestion && !manualPick[row.channelProduct.id])
                    "
                    @click="confirmRow(row)"
                  >
                    <span
                      v-if="busyIds.has(row.channelProduct.id)"
                      class="i-lucide-loader-2 size-3 animate-spin inline-block"
                    />
                    ✓ {{ row.suggestion ? 'Onayla' : 'Bağla' }}
                  </button>
                  <button
                    v-if="row.suggestion"
                    class="px-2.5 py-1.5 text-[11.5px] font-semibold rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                    :disabled="busyIds.has(row.channelProduct.id)"
                    @click="rejectRow(row)"
                  >
                    Reddet
                  </button>
                </div>
              </div>

              <!-- renk onayı satırı -->
              <div
                v-if="row.suggestion && row.suggestion.unmatchedColors.length"
                class="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900 px-3 py-2"
              >
                <span
                  class="text-[11px] font-semibold text-yellow-800 dark:text-yellow-300"
                >
                  ⚠ Eşleşmeyen renkler — karşılığını seç:
                </span>
                <span
                  v-for="c in row.suggestion.unmatchedColors"
                  :key="c"
                  class="flex items-center gap-1.5"
                >
                  <span
                    class="text-[11px] font-mono text-slate-600 dark:text-slate-300"
                    >{{ c }} →</span>
                  <select
                    v-model="colorPick[row.channelProduct.id][c]"
                    class="px-1.5 py-1 text-[11px] border border-yellow-300 dark:border-yellow-800 rounded-md bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                  >
                    <option :value="undefined">seç…</option>
                    <option
                      v-for="uc in row.suggestion.umColors"
                      :key="uc"
                      :value="uc"
                    >
                      {{ uc }}
                    </option>
                  </select>
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
